import React, { useState, useEffect, useMemo } from "react";
// Redux
import { useSelector, useDispatch } from "react-redux";
import { setCurrentHospitalName } from "../../redux/hospitalDucks";
// Constants
import { pathHomePage, WEB_SERVICE_DASHBOARD_ROUTE } from "../../config/constants";
// Components
import {
  Title,
  CreateHospitalDiv,
  CreateHospitalLink,
  CheckboxFilterDiv,
  CheckboxInput,
  CheckboxLabel,
  TableContainer,
  TableTitle,
  HospitalContainer,
  HospitalNameLink,
  HospitalInfo,
  EditHospitalLink,
  EditHospitalImg,
} from "./styles";
import Header from "../header";
import Loader from "../loader";
import UseFetch from "../../config/useFetch";
// Assets
import EditIcon from "../../assets/edit_icon.svg";

const HospitalsManagement = () => {
  const [isLoadingHospitals, setIsLoadingHospitals] = useState(true);
  const [hospitals, setHospitals] = useState([]);
  const [isCustomerHospitalsChecked, setIsCustomerHospitalsChecked] = useState(false);
  const [currentSort, setCurrentSort] = useState("");

  const dispatch = useDispatch();
  const isLoggedIn = useSelector(({ user }) => user.isLoggedIn);
  const environment = useSelector(({ database }) => database.environment);

  // Fetching hospitals data
  useEffect(() => {
    const getHospitalsSignal = new AbortController();

    if (isLoggedIn && environment) {
      UseFetch("get", `${WEB_SERVICE_DASHBOARD_ROUTE}/hospital/get-list/${environment}`, getHospitalsSignal)
        .then(({ data }) => {
          setHospitals(data);
          setIsLoadingHospitals(false);
        })
        .catch((error) => {
          console.log("Get hospitals: ", error);
        });
    }

    return () => {
      setHospitals([]);
      setIsLoadingHospitals(true);
      if (getHospitalsSignal) {
        getHospitalsSignal.abort();
      }
    };
  }, [isLoggedIn, environment]);

  const renderAddCustomerHospitalsCheckbox = () => {
    return (
      <CheckboxFilterDiv>
        <CheckboxInput
          type="checkbox"
          id="addCustomerHospitalsCheckbox"
          checked={isCustomerHospitalsChecked}
          onChange={() => setIsCustomerHospitalsChecked(!isCustomerHospitalsChecked)}
        />
        <CheckboxLabel htmlFor="addCustomerHospitalsCheckbox">Only customers</CheckboxLabel>
      </CheckboxFilterDiv>
    );
  };

  const displayedHospitals = useMemo(() => {
    let targetedHospitals = [...hospitals];

    if (isCustomerHospitalsChecked) {
      targetedHospitals = targetedHospitals.filter((hospital) => hospital.isCustomer);
    }

    if (currentSort === "subscriptionDays") {
      targetedHospitals.sort((a, b) => {
        const daysA = a.SUBSCRIPTION_DAYS || 0;
        const daysB = b.SUBSCRIPTION_DAYS || 0;
        return daysB - daysA;
      });
    }

    return targetedHospitals;
  }, [hospitals, isCustomerHospitalsChecked, currentSort]);

  const sortBySubscriptionDays = () => {
    setCurrentSort(currentSort === "subscriptionDays" ? "" : "subscriptionDays");
  };

  const renderTableHeader = () => {
    return (
      <TableContainer>
        <TableTitle>Hospital</TableTitle>
        <TableTitle width="55px">Edit</TableTitle>
        <TableTitle>Email</TableTitle>
        <TableTitle>Phone</TableTitle>
        <TableTitle>Contact</TableTitle>
        <TableTitle>Address</TableTitle>
        <TableTitle id="subscriptionDays" onClick={sortBySubscriptionDays} isMarked={currentSort === "subscriptionDays"}>
          Days of free trial (RGS+)
        </TableTitle>
      </TableContainer>
    );
  };

  const renderHospitals = () => {
    const handleSetCurrentHospitalName = (hospitalName) => {
      dispatch(setCurrentHospitalName(hospitalName));
    };

    return displayedHospitals.map((hospital) => {
      const isNull = (value) => value === null || value === "";
      const hospitalId = hospital.HOSPITAL_ID;

      return (
        <HospitalContainer key={hospitalId}>
          <HospitalNameLink
            onClick={() => handleSetCurrentHospitalName(hospital.NAME)}
            to={`${pathHomePage}/intermediate-management/${hospitalId}`}
          >
            {hospital.NAME}
          </HospitalNameLink>
          <HospitalInfo width="55px">
            <EditHospitalLink to={`${pathHomePage}/edit-hospital/${hospitalId}`}>
              <EditHospitalImg src={EditIcon} alt="edit-icon" />
            </EditHospitalLink>
          </HospitalInfo>
          <HospitalInfo>{isNull(hospital.EMAIL) ? "n/a" : hospital.EMAIL}</HospitalInfo>
          <HospitalInfo>{isNull(hospital.PHONE) ? "n/a" : hospital.PHONE}</HospitalInfo>
          <HospitalInfo>{isNull(hospital.CONTACT) ? "n/a" : hospital.CONTACT}</HospitalInfo>
          <HospitalInfo>{isNull(hospital.ADDRESS) ? "n/a" : hospital.ADDRESS}</HospitalInfo>
          <HospitalInfo>{isNull(hospital.SUBSCRIPTION_DAYS) ? "n/a" : hospital.SUBSCRIPTION_DAYS}</HospitalInfo>
        </HospitalContainer>
      );
    });
  };

  return (
    <>
      <Header />
      <Title>Manage hospitals</Title>
      <CreateHospitalDiv>
        <CreateHospitalLink to={`${pathHomePage}/create-hospital`}>Create new hospital</CreateHospitalLink>
      </CreateHospitalDiv>
      {renderAddCustomerHospitalsCheckbox()}
      {isLoadingHospitals ? (
        <Loader />
      ) : (
        <>
          {!hospitals.length ? (
            <Title>There are no hospitals</Title>
          ) : (
            <>
              {renderTableHeader()}
              {renderHospitals()}
            </>
          )}
        </>
      )}
    </>
  );
};

export default HospitalsManagement;
